import { initPostMessaging } from "../messages/channelMessageUtility";
import type {
  AccessTokenFunction,
  BootFailedError,
  OwletEventHandlers,
  HttpRequestOptions,
  InlineFrameLoadResult,
} from "../types";
import type { AdaptiveLogger } from "@1js/office-online-jsapi-core";
import { LogCategory, TraceLevel } from "@1js/office-online-jsapi-core";
import { ErrorType } from "../types";

export const formId = "SharepointDocAspxForm";
export const frameId = "SharepointDocAspxFrame";
//TODO: Add telemetry(BULS and Health), task id 8782423
export async function createInlineFrame(
  requestConfig: HttpRequestOptions,
  container: HTMLElement,
  channelId: string,
  getAuthToken: AccessTokenFunction,
  eventHandlers: OwletEventHandlers,
  logger?: AdaptiveLogger
): Promise<InlineFrameLoadResult> {
  const frameOrigin = new URL(requestConfig.url).origin;
  const frame = createFrame(container, frameId, frameOrigin);
  const sharepointForm = createForm(container, formId, frameId, requestConfig);
  try {
    logger?.ULS.sendTraceTag(
      0x1e355462 /* tag_4nvr8 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Info,
      `CreateInlineFrame: Try creating doc.aspx iframe with metadata post via http`
    );
    sharepointForm.submit();
    if (!frame.contentWindow) {
      return Promise.reject({
        isLoaded: false,
        error: {
          errorMessage: `Failed to create inline frame. [Error: contentWindow is null]`,
          errorName: "FailedCreateInlineFrame",
          errorType: ErrorType.HttpError,
        } as BootFailedError,
      });
    }

    // sets up a message port for communication between the iframe and the parent window
    initPostMessaging(
      frame,
      frameOrigin,
      frame.contentWindow,
      channelId,
      getAuthToken,
      eventHandlers,
      logger
    );

    logger?.ULS.sendTraceTag(
      0x1e355461 /* tag_4nvr7 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Info,
      `CreateInlineFrame: Successfully created Doc.aspx iframe.`
    );
    return Promise.resolve({
      isLoaded: true,
    });
  } catch (submitError: any) {
    return Promise.reject({
      isLoaded: false,
      error: {
        errorMessage: `Failed submit form to sharepoint. [Error: ${submitError}]`,
        errorName: "FailedCreateInlineFrame",
        errorType: ErrorType.HttpError,
      } as BootFailedError,
    });
  }
}

// Creates doc.aspx iFrame and returns the iFrame element
export function createFrame(
  container: HTMLElement,
  frameId: string,
  frameOrigin: string
): HTMLIFrameElement {
  const frame = document.createElement("iframe");
  frame.id = frameId;
  frame.name = frameId;
  frame.allowFullscreen = true;
  frame.setAttribute("allowTransparency", "true");
  frame.setAttribute("allow", buildPermissionsPolicy(frameOrigin));
  //Set the style of the frame element to full-screen display, with no borders, transparent background, absolute positioning, and zero padding
  frame.setAttribute(
    "style",
    "inset: 0px; height: 100%; width: 100%; display: block; position: absolute; background-color: transparent; border: none;"
  );
  container.appendChild(frame);
  return frame;
}

/**
 * Builds a cross origin allow list for sensitive features such as camera.
 */
function buildPermissionsPolicy(frameOrigin: string): string {
  return `microphone ${frameOrigin}; autoplay ${frameOrigin}; camera ${frameOrigin}; clipboard-read ${frameOrigin}; clipboard-write ${frameOrigin}; keyboard-map ${frameOrigin}; display-capture ${frameOrigin}`;
}

// Create form to safely pass data from owlet to sharepoint via http post
export function createForm(
  container: HTMLElement,
  formId: string,
  targetFrame: string,
  requestConfig: HttpRequestOptions
): HTMLFormElement {
  const form = document.createElement("form");
  form.id = formId;
  form.action = requestConfig.url;
  form.method = requestConfig.method;
  form.target = targetFrame;

  // create hidden inputs for data passed in
  const postData = requestConfig.body ? requestConfig.body : {};
  for (const [key, value] of Object.entries(postData)) {
    const element = document.createElement("input");
    element.type = "hidden";
    element.name = key;
    if (typeof value === "string") {
      element.value = value;
    } else if (typeof value === "boolean" || typeof value === "number") {
      element.value = String(value);
    } else {
      element.value = JSON.stringify(value);
    }
    form.appendChild(element);
  }

  container.appendChild(form);
  return form;
}
